// Shared "one CheckResult" renderer (check_id/name/category/severity/scope/
// status/summary/affected_count/issues[]/matrix? - see preflight.py's
// CheckResult.to_dict()), factored out of PreflightPanel.js's inline
// MatrixTable/IssuesTable/CheckHeader trio so the Phase B panels
// (DataQualityControlPanel: Loan Number Check + Investment Amount Check,
// CashflowReconciliationPanel: Cashflow Reconciliation Check) don't each
// duplicate that ~40 lines. PreflightPanel.js itself is READ ONLY (out of
// scope for this pass) and still has its own copy - see validation/README
// note in the handback report for why this isn't a full single-source-of-
// truth extraction.
//
// Adds one thing PreflightPanel.js's version doesn't need: an optional
// `onIssueClick(issue)` so a caller (CashflowReconciliationPanel) can turn
// each mismatch row into a drilldown trigger (issue.field = platform,
// issue.identifier = investment_id for that check - see
// preflight.py::check_cashflow_reconciliation_check()).

import React from 'react';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const STATUS_SEVERITY = {
  pass: 'success',
  ok: 'success',
  warning: 'warning',
  fail: 'danger',
  mismatch: 'danger',
  error: 'danger',
};

export const StatusTag = ({ status }) => <Tag value={status} severity={STATUS_SEVERITY[status] || 'info'} />;

const humanize = (columnName) => (columnName || '').replace(/_/g, ' ');

// Same "static severity property vs. live status" note as CheckResultCard
// below - only worth surfacing once a check has actually failed.
export const checkQualifierText = (check) => {
  const hasFailed = check.status !== 'pass' && check.status !== 'ok';
  let text = check.scope;
  if (hasFailed) text += ` · ${check.severity}`;
  if (check.affected_count > 0) text += ` · ${check.affected_count} affected`;
  return text;
};

export const IssuesTable = ({ issues, onIssueClick, onJumpToRow }) => {
  if (!issues || issues.length === 0) return null;
  return (
    <DataTable
      value={issues}
      size="small"
      showGridlines
      stripedRows
      onRowClick={onIssueClick ? (e) => onIssueClick(e.data) : undefined}
      rowClassName={() => (onIssueClick ? 'cr-clickable-row' : '')}
    >
      <Column
        field="identifier"
        header="Identifier"
        body={
          onJumpToRow
            ? (row) =>
                row.identifier ? (
                  <a
                    href="#view-loantape"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onJumpToRow(row.identifier);
                    }}
                  >
                    {row.identifier}
                  </a>
                ) : (
                  ''
                )
            : undefined
        }
      />
      <Column field="source_platform_loan_id" header="Originator Loan ID" />
      <Column field="field" header="Field" />
      <Column field="value" header="Value" body={(row) => String(row.value ?? '')} />
      <Column field="message" header="Message" />
      <Column field="row" header="File Row" headerClassName="cr-num" bodyClassName="cr-num" />
    </DataTable>
  );
};

export const MatrixTable = ({ matrix }) => {
  if (!matrix) return null;
  return (
    <div className="cr-table-frame">
    <table className="cr-matrix-table">
      <thead>
        <tr>
          <th></th>
          {matrix.criteria.map((c) => (
            <th key={c.id}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {matrix.rows.map((row) => (
          <tr key={row.column}>
            <td>
              {humanize(row.column)}
              {row.subtitle && <div className="cr-matrix-subtitle">{row.subtitle}</div>}
            </td>
            {matrix.criteria.map((c) => {
              const cell = row.cells[c.id];
              if (!cell) return <td key={c.id}>-</td>;
              return (
                <td key={c.id} className={`cr-matrix-cell cr-matrix-${cell.status}`}>
                  {cell.status === 'fail' ? `fail (${cell.affected_count})` : cell.status}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
};

// Full card: border + header (status/name/severity-scope-affected_count) +
// matrix-or-summary + issues table. Always-expanded (no Accordion) since the
// callers here show 1-2 checks at a time, not a long list like
// PreflightPanel.js's report.checks.
// `severity` is a static property of the check DEFINITION (how bad a
// failure of this check would be), not a live status - it's set the same
// whether the check currently passes or fails. Showing it unconditionally
// next to a passing check (e.g. "error · cashflows" on a check that shows a
// green Pass tag) reads as a live error when there isn't one, so it's only
// surfaced here once the check has actually failed.
const CheckResultCard = ({ check, onIssueClick, onJumpToRow, title }) => {
  if (!check) return null;
  return (
    <div className="cr-card mb-3">
      <div className="cr-card-header">
        <StatusTag status={check.status} />
        <strong className="cr-card-title">{title || check.name}</strong>
        <span className="cr-check-qualifier">{checkQualifierText(check)}</span>
      </div>
      {check.matrix ? <MatrixTable matrix={check.matrix} /> : <p className="cr-check-summary">{check.summary}</p>}
      {check.issues_truncated && (
        <p className="cr-warning-text">
          Showing a truncated subset of issues per field - see the full export for the rest.
        </p>
      )}
      {onIssueClick && check.issues && check.issues.length > 0 && (
        <p className="cr-hint-text">Click a row to see the drilldown.</p>
      )}
      <IssuesTable issues={check.issues} onIssueClick={onIssueClick} onJumpToRow={onJumpToRow} />
    </div>
  );
};

export default CheckResultCard;
